import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MaterialModule } from '../../shared/material.module';
import { GalleryService } from '../../services/gallery.service';
import { AuthService } from '../../services/auth.service';
import { CardsService } from '../../services/cards.service';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { TruncatePipe } from 'src/app/services/truncate.pipe';
import { BreadcrumbComponent } from 'src/app/components/shared/breadcrumb/breadcrumb.component';

export interface GalleryImage {
  id?: string;
  cardId: string;
  imageUrl: string;
  createdAt?: string;
  updatedAt?: string;
}

type ViewMode = 'grid' | 'list';

@Component({
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  standalone: true,
  imports: [CommonModule, MaterialModule, TruncatePipe, BreadcrumbComponent],
  styleUrls: ['./gallery.component.scss'],
})
export class GalleryComponent implements OnInit {
  galleryImages: GalleryImage[] = [];
  loading = false;
  error = '';
  viewMode: ViewMode = 'grid';
  showUploadDialog = false;
  showImageModal = false;
  currentImageUrl = '';

  // File upload state
  selectedFiles: File[] = [];
  isDragging = false;
  uploading = false;
  uploadProgress = 0;
  uploadedCount = 0;
  maxImages = 10;
  filePreviews: Map<File, string> = new Map();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private galleryService: GalleryService,
    private cardsService: CardsService,
    private storageService: FirebaseStorageService
  ) {}

  ngOnInit() {
    this.fetchGalleryImages();
  }

  get currentCardId(): string | null {
    return this.cardsService.selectedCard()?.id ?? null;
  }

  fetchGalleryImages(): void {
    const cardId = this.currentCardId;
    if (!cardId) {
      this.error = 'No card selected';
      return;
    }

    this.loading = true;
    this.error = '';
    this.galleryService.getGalleryImages(cardId).subscribe({
      next: (res) => {
        this.galleryImages = res;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to fetch gallery images';
        this.loading = false;
        console.error('Error fetching gallery images:', err);
      },
    });
  }

  openUploadDialog(): void {
    if (this.galleryImages.length >= this.maxImages) {
    alert(`You can only keep ${this.maxImages} images in the gallery.`);
    return;
  }
    this.showUploadDialog = true;
    this.selectedFiles = [];
    this.filePreviews.clear();
  }

  closeUploadDialog(): void {
    this.showUploadDialog = false;
  }

  openImageModal(imageUrl: string): void {
    this.currentImageUrl = imageUrl;
    this.showImageModal = true;
  }

  closeImageModal(): void {
    this.showImageModal = false;
    this.currentImageUrl = '';
  }

  onFileSelected(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (files) {
      this.selectedFiles = Array.from(files);
      this.generatePreviews();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;

    if (event.dataTransfer?.files) {
      this.selectedFiles = Array.from(event.dataTransfer.files);
      this.generatePreviews();
    }
  }

  generatePreviews(): void {
    this.filePreviews.clear();
    this.selectedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreviews.set(file, e.target?.result as string);
      };
      reader.readAsDataURL(file);
    });
  }

  getFilePreview(file: File): string {
    return this.filePreviews.get(file) || '';
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  async uploadImages(): Promise<void> {
    const cardId = this.currentCardId;
    if (!cardId) {
      this.error = 'No card selected';
      return;
    }
    const remainingSlots = this.maxImages - this.galleryImages.length;
    if (remainingSlots <= 0) {
    alert(`Gallery already holds the maximum of ${this.maxImages} images.`);
    return;
  }
  if (this.selectedFiles.length > remainingSlots) {
    alert(`Only ${remainingSlots} more image${remainingSlots === 1 ? '' : 's'} can be added.`);
    this.selectedFiles = this.selectedFiles.slice(0, remainingSlots);
  }
    if (this.selectedFiles.length === 0) {
      return;
    }

    this.uploading = true;
    this.uploadProgress = 0;
    this.uploadedCount = 0;

    try {
      const imageUrls: string[] = [];

      // Process files sequentially with progress updates
      for (let i = 0; i < this.selectedFiles.length; i++) {
        const file = this.selectedFiles[i];
        const url = await this.storageService.uploadFile(
          `gallery/${cardId}`,
          file,
          (progress) => {
            // Update progress for current file
            this.uploadProgress = (i + progress / 100) / this.selectedFiles.length * 100;
          }
        );
        imageUrls.push(url);
        this.uploadedCount = i + 1;
      }

      // Add images to gallery via service
      await this.galleryService.addGalleryImages(cardId, imageUrls).toPromise();

      this.closeUploadDialog();
      this.fetchGalleryImages();
    } catch (err) {
      this.error = 'Failed to upload images';
      console.error('Upload error:', err);
    } finally {
      this.uploading = false;
    }
  }

  deleteImage(imageUrl: string): void {
    if (!confirm('Are you sure you want to delete this image?')) {
      return;
    }

    const cardId = this.currentCardId;
    console.log('Image URL being sent:', imageUrl);
    if (!cardId) {
      this.error = 'No card selected';
      return;
    }

    this.loading = true;
    this.galleryService.removeGalleryImage(cardId, imageUrl).subscribe({
      next: () => {
        this.fetchGalleryImages();
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to delete image';
        this.loading = false;
        console.error('Delete error:', err);
      },
    });
  }

  confirmDeleteAll(): void {
    if (!confirm('Are you sure you want to delete ALL images in this gallery? This cannot be undone.')) {
      return;
    }

    const cardId = this.currentCardId;
    if (!cardId) {
      this.error = 'No card selected';
      return;
    }

    this.loading = true;
    this.galleryService.deleteAllGalleryImages(cardId).subscribe({
      next: () => {
        this.fetchGalleryImages();
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to delete gallery images';
        this.loading = false;
        console.error('Delete all error:', err);
      },
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) {
      return 'N/A';
    }
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  }

  trackByImageUrl(_index: number, image: GalleryImage): string {
    return image.imageUrl;
  }
}